import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  TablePagination,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { TopupRequestForm } from '@/components/balance/TopupRequestForm'
import { TopupRequestsList } from '@/components/balance/TopupRequestsList'
import { BalanceTransactionsList } from '@/components/balance/BalanceTransactionsList'
import { formatMoney } from '@/components/billing/formatMoney'
import { teacherBalanceApi } from '@/api/teacherBalance'
import { useBalanceStore } from '@/stores/balanceStore'
import { getErrorMessage } from '@/utils/error'
import type { BalanceTopupRequest, TeacherBalanceTransaction } from '@/types/tts'

export function TeacherBalancePage() {
  const { t } = useTranslation()
  const balance = useBalanceStore((s) => s.balance)
  const currency = useBalanceStore((s) => s.currency)
  const pricePerChar = useBalanceStore((s) => s.pricePerChar)
  const cacheDiscountPercent = useBalanceStore((s) => s.cacheDiscountPercent)
  const pendingTopupTotal = useBalanceStore((s) => s.pendingTopupTotal)
  const fetchBalance = useBalanceStore((s) => s.fetchBalance)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [requests, setRequests] = useState<BalanceTopupRequest[]>([])
  const [requestsPage, setRequestsPage] = useState(1)
  const [requestsPerPage, setRequestsPerPage] = useState(15)
  const [requestsTotal, setRequestsTotal] = useState(0)
  const [transactions, setTransactions] = useState<TeacherBalanceTransaction[]>([])
  const [transactionsPage, setTransactionsPage] = useState(1)
  const [transactionsPerPage, setTransactionsPerPage] = useState(15)
  const [transactionsTotal, setTransactionsTotal] = useState(0)
  const [cancellingId, setCancellingId] = useState<number | null>(null)

  const loadAll = useCallback(async (
    topupPage: number,
    topupPerPage: number,
    txPage: number,
    txPerPage: number,
  ) => {
    setLoading(true)
    setError(null)
    try {
      await fetchBalance()
      const [requestsResult, transactionsResult] = await Promise.all([
        teacherBalanceApi.listTopupRequests(topupPage, topupPerPage),
        teacherBalanceApi.getTransactions(txPage, txPerPage),
      ])
      setRequests(requestsResult.data)
      setRequestsTotal(requestsResult.meta?.total ?? requestsResult.data.length)
      setTransactions(transactionsResult.data)
      setTransactionsTotal(transactionsResult.meta?.total ?? transactionsResult.data.length)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [fetchBalance, t])

  useEffect(() => {
    void loadAll(requestsPage, requestsPerPage, transactionsPage, transactionsPerPage)
  }, [loadAll, requestsPage, requestsPerPage, transactionsPage, transactionsPerPage])

  const handleCancel = async (request: BalanceTopupRequest) => {
    setCancellingId(request.id)
    setError(null)
    try {
      const cancelled = await teacherBalanceApi.cancelTopupRequest(request.id)
      setRequests((prev) => prev.map((r) => (r.id === cancelled.id ? cancelled : r)))
      await fetchBalance().catch((err) => {
        setError(getErrorMessage(err) || t('common.error'))
      })
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setCancellingId(null)
    }
  }

  const handleCreated = (_request: BalanceTopupRequest) => {
    setRequestsPage(1)
    void loadAll(1, requestsPerPage, transactionsPage, transactionsPerPage)
  }

  return (
    <ProfileLayout>
      <Box sx={{ p: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('balance.title', 'Баланс')}
          </Typography>
          <PageHelpPopover pageKey="profile-balance" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {t('balance.current', 'Текущий баланс')}
                </Typography>
                <Typography variant="h4">
                  {balance !== null ? formatMoney(balance, currency) : '—'}
                </Typography>
                {pendingTopupTotal !== null && parseFloat(pendingTopupTotal) > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('balance.pendingTopups', 'Ожидает подтверждения')}:{' '}
                    {formatMoney(pendingTopupTotal, currency)}
                  </Typography>
                )}
                {pricePerChar !== null && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('balance.pricePerChar', 'Озвучивание')}: {formatMoney(pricePerChar, currency)}
                    {' / '}
                    {t('balance.perChar', 'символ')}
                    {cacheDiscountPercent ? (
                      <>
                        {' · '}
                        {t('balance.cacheDiscount', 'повтор −{{percent}}%', {
                          percent: cacheDiscountPercent,
                        })}
                      </>
                    ) : null}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('balance.topup.title', 'Заявка на пополнение')}
                </Typography>
                <TopupRequestForm onCreated={handleCreated} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {loading ? (
          <>
            {[1, 2].map((card) => (
              <Card key={card} sx={{ mb: 3 }}>
                <CardContent>
                  <Skeleton variant="text" width="30%" height={32} sx={{ mb: 1 }} />
                  {[1, 2, 3].map((row) => (
                    <Skeleton key={row} variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />
                  ))}
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('balance.topup.listTitle', 'Мои заявки')}
                </Typography>
                <TopupRequestsList
                  requests={requests}
                  onCancel={handleCancel}
                  cancellingId={cancellingId}
                />
                {requestsTotal > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', minWidth: 0 }}>
                    <TablePagination
                      component="div"
                      count={requestsTotal}
                      page={Math.max(0, requestsPage - 1)}
                      onPageChange={(_, page) => setRequestsPage(page + 1)}
                      rowsPerPage={requestsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRequestsPerPage(parseInt(e.target.value, 10))
                        setRequestsPage(1)
                      }}
                      rowsPerPageOptions={[10, 15, 25, 50]}
                      labelRowsPerPage={t('words.rows_per_page')}
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} ${t('words.of')} ${count !== -1 ? count : `${t('words.more_than')} ${to}`}`
                      }
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('balance.transactions.title', 'История операций')}
                </Typography>
                <BalanceTransactionsList transactions={transactions} />
                {transactionsTotal > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', minWidth: 0 }}>
                    <TablePagination
                      component="div"
                      count={transactionsTotal}
                      page={Math.max(0, transactionsPage - 1)}
                      onPageChange={(_, page) => setTransactionsPage(page + 1)}
                      rowsPerPage={transactionsPerPage}
                      onRowsPerPageChange={(e) => {
                        setTransactionsPerPage(parseInt(e.target.value, 10))
                        setTransactionsPage(1)
                      }}
                      rowsPerPageOptions={[10, 15, 25, 50]}
                      labelRowsPerPage={t('words.rows_per_page')}
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} ${t('words.of')} ${count !== -1 ? count : `${t('words.more_than')} ${to}`}`
                      }
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </ProfileLayout>
  )
}

export default TeacherBalancePage
